import type { RewardOption } from '@/components/admin/mail/reward-options';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  claimDeadline,
  formatKst,
  kstLocalToEpochMs,
  type PaymentEventDraft,
  type ValidationIssue,
} from './payment-event.model';
import { TierEditor } from './TierEditor';

interface Props {
  draft: PaymentEventDraft;
  options: RewardOption[];
  issues: ValidationIssue[];
  saving: boolean;
  onChange: (next: PaymentEventDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}

export function PaymentEventForm({
  draft,
  options,
  issues,
  saving,
  onChange,
  onSave,
  onCancel,
}: Props) {
  const patch = (next: Partial<PaymentEventDraft>) => onChange({ ...draft, ...next });
  const messageFor = (field: string) => issues.find((x) => x.field === field)?.message;

  const start = kstLocalToEpochMs(draft.startLocal);
  const end = kstLocalToEpochMs(draft.endLocal);
  const deadline =
    end === null ? null : claimDeadline({ end_at: end, grace_days: draft.grace_days });

  return (
    <div className="flex flex-col gap-4 rounded-md border border-border p-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pe-id">이벤트 id</Label>
          <Input id="pe-id" className="w-24" value={draft.id} disabled />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pe-start">시작 (KST)</Label>
          <Input
            id="pe-start"
            type="datetime-local"
            className="w-56"
            disabled={saving}
            value={draft.startLocal}
            onChange={(e) => patch({ startLocal: e.target.value })}
          />
          <span className="text-xs text-muted-foreground">{start ?? '—'}</span>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pe-end">종료 (KST)</Label>
          <Input
            id="pe-end"
            type="datetime-local"
            className="w-56"
            disabled={saving}
            value={draft.endLocal}
            onChange={(e) => patch({ endLocal: e.target.value })}
          />
          <span className="text-xs text-muted-foreground">{end ?? '—'}</span>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pe-grace">유예일</Label>
          <Input
            id="pe-grace"
            type="number"
            min={0}
            className="w-24"
            disabled={saving}
            value={draft.grace_days}
            onChange={(e) => patch({ grace_days: Number(e.target.value) })}
          />
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        클레임 마감: {deadline === null ? '—' : formatKst(deadline)} (종료 + 유예일). 이 시각 이후
        서버가 클레임을 거절합니다.
      </p>

      {(['start', 'end', 'grace', 'id', 'tiers'] as const).map((f) => {
        const msg = messageFor(f);
        return msg ? (
          <p key={f} className="text-xs text-rose-600 dark:text-rose-400">
            {msg}
          </p>
        ) : null;
      })}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="pe-title">타이틀 l10n 키 (선택)</Label>
        <Input
          id="pe-title"
          className="w-96"
          placeholder="비우면 PAYMENT_EVENT_TITLE 사용"
          disabled={saving}
          value={draft.title_key}
          onChange={(e) => patch({ title_key: e.target.value })}
        />
        {draft.title_key.trim() ? (
          <span className="text-xs text-amber-600 dark:text-amber-400">
            이 키를 /l10n-sheet 로 발행하지 않으면 게임 화면에 RAW KEY 가 그대로 노출됩니다.
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">
            비워두면 기본 키 PAYMENT_EVENT_TITLE 로 폴백합니다.
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>티어</Label>
        <span className="text-xs text-muted-foreground">
          저장 시 VX 임계값 오름차순으로 자동 정렬됩니다. 보상 드롭다운의 펫·캐릭터 목록은 어드민
          빌드의 sheet env 기준이라 선택한 env 와 다를 수 있습니다 — 없는 키는 "직접 입력"으로
          넣으세요.
        </span>
        <TierEditor
          tiers={draft.tiers}
          options={options}
          issues={issues}
          disabled={saving}
          onChange={(tiers) => patch({ tiers })}
        />
      </div>

      <div className="flex items-center gap-2">
        <Button type="button" disabled={saving || issues.length > 0} onClick={onSave}>
          저장
        </Button>
        <Button type="button" variant="outline" disabled={saving} onClick={onCancel}>
          취소
        </Button>
      </div>
    </div>
  );
}
